import { ChangeEvent, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { getWebBoosterVairant } from '../../../constants/webBoosterType';
import { WebPageType } from '../../../constants/webPageType';
import useLocale from '../../../hooks/useLocale';
import { AlbumPartition, Space } from '../../../models/SpaceStore';
import { WebBoosterType } from '../../../models/UserStore';
import {
  CircleCheckbox,
  FolderBreadcrumbText,
  GeneralModal,
  GeneralModalNoButtonVariant,
  GeneralModalYesButtonVariant,
  JoyajoyImage,
  TextInput,
} from '../../Molecules';
import { BasicPopupContainer } from '../Containers/BasicPopupContainer';

import styles from './ClubSelectFolderPopup.module.css';

type TextInputProps = {
  value: string;
  errorMessage?: string;
  maxLength: number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

type ClubSelectFoldeContent = {
  webBoosterType: WebBoosterType | undefined;
  space: Space;
  selectedFolderId: string;
  folders: AlbumPartition[];
  newFolder: TextInputProps | undefined;
  onClickSelect: (item: string) => void;
};

type ClubSelectFolderPopupProps = ClubSelectFoldeContent & {
  onConfirm: () => void;
  onCancel: () => void;
};

const ClubSelectFolderContent = ({
  webBoosterType,
  space,
  selectedFolderId,
  folders,
  newFolder,
  type,
  onClickSelect,
}: ClubSelectFoldeContent & { type: 'modal' | 'popup' }) => {
  const { t } = useTranslation();

  const l = useLocale();
  const spaceIconSource = space?.icon?.publicUrls?.small ?? '';
  const spaceI18nInfo = l(space.i18nClassroomInfo);

  const isNewFolder = selectedFolderId === `${FolderBreadcrumbText.NewFolder}`;
  const isDefaultFolder =
    selectedFolderId === `${webBoosterType}-${FolderBreadcrumbText.Default}`;
  const isAllFolder =
    selectedFolderId === `${webBoosterType}-${FolderBreadcrumbText.All}`;

  const selectedFolder = folders.find(
    (folder) => folder.partitionId === selectedFolderId,
  );

  return (
    <div
      className={type === 'modal' ? styles.modalContent : styles.popupContent}
    >
      <div className={styles.titleImageContainer}>
        <div className={styles.imageWrapper}>
          <JoyajoyImage
            resource={{ uri: spaceIconSource }}
            className={styles.image}
          />
        </div>
        <div className={styles.titleContainer}>
          <div className={styles.spaceName}>{spaceI18nInfo?.name}</div>
          <div className={styles.subtitle}>
            {t(getWebBoosterVairant(webBoosterType).name)}
          </div>
          <p className={styles.folderName}>
            {`> `}
            <span
              style={{
                color: isAllFolder ? '#659799' : undefined,
                fontWeight: isAllFolder ? 'bold' : undefined,
              }}
            >
              {isNewFolder
                ? newFolder?.value
                : isDefaultFolder
                ? t('Default Folder')
                : isAllFolder
                ? t('All Content')
                : l(selectedFolder?.i18nGameSetPartitionInfo)?.name}
            </span>
          </p>
        </div>
      </div>

      <hr />

      <p className={styles.sectionTitle}>{t('Folder')}</p>
      {newFolder ? (
        <>
          <hr />
          <div
            className={styles.folderItem}
            onClick={() => onClickSelect(FolderBreadcrumbText.NewFolder)}
          >
            <TextInput
              value={newFolder.value}
              onChange={newFolder.onChange}
              placeholder={t('New Folder')}
              style={{
                backgroundColor: '#f1f1f1',
                color: '#000',
              }}
              maxLength={newFolder.maxLength}
              errorMessage={newFolder.errorMessage}
            />

            <CircleCheckbox
              checked={isNewFolder}
              webPageType={WebPageType.JOYAJOY}
            />
          </div>
        </>
      ) : null}
      <div className={styles.folderList}>
        {folders.map((item, index) => (
          <Fragment key={index}>
            <hr />
            <div
              className={styles.folderItem}
              onClick={() => onClickSelect(item.partitionId)}
              style={{
                marginBottom: index === folders.length - 1 ? '-0.5rem' : '',
              }}
            >
              {item.partitionId ===
              `${webBoosterType}-${FolderBreadcrumbText.All}` ? (
                <div className={styles.allFolderRow}>
                  <p className={styles.allFolderRowTitle}>{t('All Content')}</p>
                  <p className={styles.allFolderRowDescription}>
                    {t('Content in All Folders')}
                  </p>
                </div>
              ) : (
                <p className={styles.folderItemTitle}>{`> ${
                  item.partitionId ===
                  `${webBoosterType}-${FolderBreadcrumbText.Default}`
                    ? t('Default Folder')
                    : l(item.i18nGameSetPartitionInfo).name
                }`}</p>
              )}

              <CircleCheckbox
                checked={selectedFolderId === item.partitionId}
                webPageType={WebPageType.JOYAJOY}
              />
            </div>
          </Fragment>
        ))}
      </div>
      <div />
    </div>
  );
};

export const ClubSelectFolderPopup = (props: ClubSelectFolderPopupProps) => {
  const { t } = useTranslation();
  const { onConfirm, onCancel, ...rest } = props;

  return (
    <BasicPopupContainer
      primaryButtonLabel={t('OK')}
      secondaryButtonLabel={t('Cancel')}
      onPrimaryButtonClick={onConfirm}
      onSecondaryButtonClick={onCancel}
    >
      <ClubSelectFolderContent type="popup" {...rest} />
    </BasicPopupContainer>
  );
};

export const ClubSelectFolderModal = (props: ClubSelectFolderPopupProps) => {
  const { t } = useTranslation();
  const { onConfirm, onCancel, ...rest } = props;

  return (
    <GeneralModal
      isYesNo={true}
      onYes={onConfirm}
      onClose={onCancel}
      buttonSetting={{
        noText: t('Cancel'),
        noVariant: GeneralModalNoButtonVariant.Gray,
        yesText: t('OK'),
        yesVariant: GeneralModalYesButtonVariant.Cyan,
      }}
    >
      <ClubSelectFolderContent type="modal" {...rest} />
    </GeneralModal>
  );
};
